/**
 * Period window + compare resolution for analytics dashboards.
 * Partial periods compare with the same elapsed calendar days in the prior period.
 */
import type {
  CompareMode,
  CompareWindow,
  DateWindow,
  PeriodGrain,
  PeriodPreset,
  PeriodSpec,
  PeriodWindow,
} from "@/analytics/core/types";
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  endOfQuarter,
  endOfYear,
  format,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subYears,
} from "date-fns";
import { toZonedTime } from "date-fns-tz";

export const WAREHOUSE_TIMEZONE = "America/Toronto";

export function nowToronto(): Date {
  return toZonedTime(new Date(), WAREHOUSE_TIMEZONE);
}

export function toIsoDate(d: Date): string {
  return format(d, "yyyy-MM-dd");
}

function parseIso(iso: string): Date {
  return parseISO(iso);
}

function weekMonday(d: Date): string {
  return toIsoDate(startOfWeek(d, { weekStartsOn: 1 }));
}

function monthFirst(d: Date): string {
  return toIsoDate(startOfMonth(d));
}

function quarterFirst(d: Date): string {
  return toIsoDate(startOfQuarter(d));
}

function yearFirst(d: Date): string {
  return toIsoDate(startOfYear(d));
}

function monthStartsInQuarterThrough(endDate: Date, quarterAnchor: Date): string[] {
  const result: string[] = [];
  let cursor = startOfMonth(startOfQuarter(quarterAnchor));
  const end = startOfMonth(endDate);

  while (cursor <= end) {
    result.push(toIsoDate(cursor));
    cursor = startOfMonth(addMonths(cursor, 1));
  }

  return result;
}

function monthStartsInYearThrough(endDate: Date, yearAnchor: Date): string[] {
  const result: string[] = [];
  let cursor = startOfMonth(startOfYear(yearAnchor));
  const end = startOfMonth(endDate);
  const year = cursor.getFullYear();

  while (cursor <= end && cursor.getFullYear() === year) {
    result.push(toIsoDate(cursor));
    cursor = startOfMonth(addMonths(cursor, 1));
  }

  return result;
}

export function inferGrainFromPreset(preset: PeriodPreset): PeriodGrain {
  if (preset.endsWith("_week")) return "week";
  if (preset.endsWith("_month")) return "month";
  if (preset.endsWith("_year")) return "year";
  return "quarter";
}

/** Q1 2026 style label for a quarter-start ISO date. */
export function formatQuarterLabel(quarterStart: string): string {
  const [yearPart, monthPart] = quarterStart.split("-");
  const year = Number(yearPart);
  const month = Number(monthPart);
  const quarter = Math.floor((month - 1) / 3) + 1;
  return `Q${quarter} ${year}`;
}

export function quarterStartFromIso(isoDate: string): string {
  const [yearPart, monthPart] = isoDate.split("-");
  const year = Number(yearPart);
  const month = Number(monthPart);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return isoDate;
  const quarterMonth = Math.floor((month - 1) / 3) * 3 + 1;
  return `${year}-${String(quarterMonth).padStart(2, "0")}-01`;
}

/** Named quarter shortcuts for period preset dropdowns (current + prior three). */
export function buildRecentQuarterPresetOptions(
  count = 4,
  asOf = nowToronto(),
): { quarterStart: string; label: string }[] {
  const current = startOfQuarter(asOf);
  return Array.from({ length: count }, (_, index) => {
    const quarterStart = toIsoDate(subQuarters(current, index));
    return { quarterStart, label: formatQuarterLabel(quarterStart) };
  });
}

function formatPeriodLabel(grain: PeriodGrain, from: string, isPartial: boolean): string {
  const fromDate = parseIso(from);
  if (grain === "week") {
    const base = `Week of ${format(fromDate, "MMM d")}`;
    return isPartial ? `${base} (partial)` : base;
  }
  if (grain === "month") {
    const base = format(fromDate, "MMMM yyyy");
    return isPartial ? `${base} (partial)` : base;
  }
  if (grain === "year") {
    const base = format(fromDate, "yyyy");
    return isPartial ? `${base} (partial)` : base;
  }
  const base = `Q${Math.floor(fromDate.getMonth() / 3) + 1} ${format(fromDate, "yyyy")}`;
  return isPartial ? `${base} (partial)` : base;
}

/** Resolve primary period window from spec. */
export function resolvePeriodWindow(spec: PeriodSpec, asOf = nowToronto()): PeriodWindow {
  const asOfIso = toIsoDate(asOf);

  if (spec.preset !== "custom") {
    const grain = inferGrainFromPreset(spec.preset);

    switch (spec.preset) {
      case "this_week": {
        const from = weekMonday(asOf);
        const sunday = toIsoDate(addDays(parseIso(from), 6));
        const to = asOfIso < sunday ? asOfIso : sunday;
        return {
          grain,
          from,
          to,
          periodStart: from,
          isPartial: to < sunday,
          label: formatPeriodLabel(grain, from, to < sunday),
        };
      }
      case "last_week": {
        const thisMonday = parseIso(weekMonday(asOf));
        const from = toIsoDate(subDays(thisMonday, 7));
        const to = toIsoDate(subDays(thisMonday, 1));
        return {
          grain,
          from,
          to,
          periodStart: from,
          isPartial: false,
          label: formatPeriodLabel(grain, from, false),
        };
      }
      case "this_month": {
        const from = monthFirst(asOf);
        const monthEnd = toIsoDate(endOfMonth(asOf));
        const to = asOfIso < monthEnd ? asOfIso : monthEnd;
        return {
          grain,
          from,
          to,
          periodStart: from,
          isPartial: to < monthEnd,
          label: formatPeriodLabel(grain, from, to < monthEnd),
        };
      }
      case "last_month": {
        const prior = subMonths(asOf, 1);
        const from = monthFirst(prior);
        const to = toIsoDate(endOfMonth(prior));
        return {
          grain,
          from,
          to,
          periodStart: from,
          isPartial: false,
          label: formatPeriodLabel(grain, from, false),
        };
      }
      case "this_quarter": {
        const from = quarterFirst(asOf);
        const quarterEnd = toIsoDate(endOfQuarter(asOf));
        const to = asOfIso < quarterEnd ? asOfIso : quarterEnd;
        return {
          grain,
          from,
          to,
          periodStart: from,
          isPartial: to < quarterEnd,
          label: formatPeriodLabel(grain, from, to < quarterEnd),
        };
      }
      case "last_quarter": {
        const prior = subQuarters(asOf, 1);
        const from = quarterFirst(prior);
        const to = toIsoDate(endOfQuarter(prior));
        return {
          grain,
          from,
          to,
          periodStart: from,
          isPartial: false,
          label: formatPeriodLabel(grain, from, false),
        };
      }
      case "this_year": {
        const from = yearFirst(asOf);
        const yearEnd = toIsoDate(endOfYear(asOf));
        const to = asOfIso < yearEnd ? asOfIso : yearEnd;
        return {
          grain,
          from,
          to,
          periodStart: from,
          isPartial: to < yearEnd,
          label: formatPeriodLabel(grain, from, to < yearEnd),
        };
      }
    }
  }

  const grain = spec.grain ?? "week";
  const customStart = spec.customStart;
  if (!customStart) {
    throw new Error("custom period requires customStart");
  }

  let from: string;
  let to: string;
  let isPartial = false;

  if (grain === "week") {
    from = weekMonday(parseIso(customStart));
    const weekEnd = toIsoDate(addDays(parseIso(from), 6));
    if (asOfIso >= from && asOfIso <= weekEnd) {
      to = asOfIso;
      isPartial = to < weekEnd;
    } else {
      to = weekEnd;
    }
  } else if (grain === "month") {
    from = monthFirst(parseIso(customStart));
    const monthEnd = toIsoDate(endOfMonth(parseIso(from)));
    if (asOfIso >= from && asOfIso <= monthEnd) {
      to = asOfIso;
      isPartial = to < monthEnd;
    } else {
      to = monthEnd;
    }
  } else if (grain === "quarter") {
    from = quarterFirst(parseIso(customStart));
    const quarterEnd = toIsoDate(endOfQuarter(parseIso(from)));
    if (asOfIso >= from && asOfIso <= quarterEnd) {
      to = asOfIso;
      isPartial = to < quarterEnd;
    } else {
      to = quarterEnd;
    }
  } else {
    from = yearFirst(parseIso(customStart));
    const yearEnd = toIsoDate(endOfYear(parseIso(from)));
    if (asOfIso >= from && asOfIso <= yearEnd) {
      to = asOfIso;
      isPartial = to < yearEnd;
    } else {
      to = yearEnd;
    }
  }

  return {
    grain,
    from,
    to,
    periodStart: grain === "week" ? from : grain === "month" ? from : from,
    isPartial,
    label: formatPeriodLabel(grain, from, isPartial),
  };
}

function shiftPeriodStartBack(grain: PeriodGrain, periodStart: string): string {
  const anchor = parseIso(periodStart);
  if (grain === "week") return toIsoDate(subDays(anchor, 7));
  if (grain === "month") return monthFirst(subMonths(anchor, 1));
  if (grain === "year") return yearFirst(subYears(anchor, 1));
  return quarterFirst(subQuarters(anchor, 1));
}

function elapsedDays(window: DateWindow): number {
  return differenceInCalendarDays(parseIso(window.to), parseIso(window.from)) + 1;
}

/** Default compare window for a primary period (same elapsed days when partial). */
export function resolveDefaultCompare(
  period: PeriodWindow,
  mode: CompareMode = "prev_period",
): CompareWindow {
  if (mode === "prev_year") {
    const fromDate = parseIso(period.from);
    const toDate = parseIso(period.to);
    const from = toIsoDate(addMonths(fromDate, -12));
    const to = toIsoDate(addMonths(toDate, -12));
    return { mode, from, to, label: "Same period last year" };
  }

  const priorStart = shiftPeriodStartBack(period.grain, period.periodStart);

  if (period.isPartial) {
    const days = elapsedDays(period);
    const from = priorStart;
    const to = toIsoDate(addDays(parseIso(from), days - 1));
    return {
      mode: "prev_period",
      from,
      to,
      label: `Prior ${period.grain} (same ${days} days)`,
    };
  }

  if (period.grain === "week") {
    return {
      mode: "prev_period",
      from: priorStart,
      to: toIsoDate(addDays(parseIso(priorStart), 6)),
      label: "Prior week",
    };
  }

  if (period.grain === "month") {
    const from = priorStart;
    const to = toIsoDate(endOfMonth(parseIso(from)));
    return { mode: "prev_period", from, to, label: "Prior month" };
  }

  if (period.grain === "year") {
    const from = priorStart;
    const to = toIsoDate(endOfYear(parseIso(from)));
    return { mode: "prev_period", from, to, label: "Prior year" };
  }

  const from = priorStart;
  const to = toIsoDate(endOfQuarter(parseIso(from)));
  return { mode: "prev_period", from, to, label: "Prior quarter" };
}

/** Union of primary, compare, and trend lookback for data fetch. */
export function resolveFetchWindow(
  period: PeriodWindow,
  compare: CompareWindow,
  trendLookbackDays = 56,
): DateWindow {
  const trendFrom = toIsoDate(subDays(parseIso(period.from), trendLookbackDays));
  const candidates = [trendFrom, period.from, compare.from];
  const toCandidates = [period.to, compare.to];
  return {
    from: candidates.sort()[0]!,
    to: toCandidates.sort().at(-1)!,
  };
}

/** Month starts within a quarter window (for quarter-grain team-period metrics). */
export function monthStartsInQuarterWindow(quarterStartIso: string, throughIso: string): string[] {
  return monthStartsInQuarterThrough(parseIso(throughIso), parseIso(quarterStartIso));
}

/** Month starts within a calendar-year window (for year-grain team-period metrics). */
export function monthStartsInYearWindow(yearStartIso: string, throughIso: string): string[] {
  return monthStartsInYearThrough(parseIso(throughIso), parseIso(yearStartIso));
}

export { monthStartsInQuarterThrough };
